import { useCallback, useEffect, useState } from "react"

import {
    Box,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Tooltip,
} from "@mui/material"

import {
    DataGridPro,
    ptBR,
    gridClasses,
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarExport,
} from "@mui/x-data-grid-pro"

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"

import { ptBR as corePtBR } from "@mui/material/locale"
import { createTheme, ThemeProvider } from "@mui/material/styles"

import api from "../../../api"

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"

import dayjs from "dayjs"
import "dayjs/locale/pt-br"

import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CancelIcon from "@mui/icons-material/Cancel"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import ErrorIcon from "@mui/icons-material/Error"

import Loading from "../../Loading"
import useNotification from "../../Notification"


const theme = createTheme(
    {
        palette: {
            primary: {
                main: "#21446C",
            },
        },

        components: {
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        backgroundColor: "#fff",
                    },
                },
            },
        },
    },
    ptBR,
    corePtBR
)


const WhatsApp = () => {
    const [loading, setLoading] = useState(false)

    const [period, setPeriod] = useState(dayjs())

    const [data, setData] = useState([])

    const [personalCardDialog, setPersonalCardDialog] = useState(null)
    const [personalCardDate, setPersonalCardDate] = useState(dayjs())

    const [chargedDialog, setChargedDialog] = useState(null)
    const [chargedDate, setChargedDate] = useState(dayjs())

    /*
     * Fetch data
     */
    const fetchData = useCallback(async () => {
        setLoading(true)

        try {
            const res = await api.get("api/metrics/whatsapp/", {
                params: {
                    month: period.format("MM"),
                    year: period.format("YYYY"),
                },
            })

            if (res.status === 200) {
                setData(res.data)
                return
            }

            useNotification(
                "Ops",
                "Algo deu errado ao buscar os dados.",
                "danger"
            )
        } catch (err) {
            console.error(err)

            useNotification(
                "Ops",
                "Algo deu errado ao buscar os dados.",
                "danger"
            )
        } finally {
            setLoading(false)
        }
    }, [period])


    useEffect(() => {
        fetchData()
    }, [fetchData])


    /*
     * Rows
     */
    const rows = data.map((item, index) => ({
        id: item.id || index,
        ...item,
    }))


    /*
     * Calculate growth
     *
     * Returns:
     *   positive number -> growth
     *   negative number -> reduction
     *   null            -> cannot calculate
     *
     * Example:
     *   current = 120
     *   previous = 100
     *   => 20
     */
    const calculateGrowth = (current, previous) => {
        current = Number(current) || 0
        previous = Number(previous)

        if (!Number.isFinite(previous) || previous === 0) {
            return null
        }

        return ((current - previous) / previous) * 100
    }


    /*
     * Growth renderer
     *
     * Example:
     *
     * 1.250  ↑ 12,5%
     *
     * Positive = green
     * Negative = red
     * Zero     = gray
     */
    const renderGrowth = (current, previous) => {
        const growth = calculateGrowth(current, previous)

        if (growth === null) {
            return null
        }

        const isPositive = growth > 0
        const isNegative = growth < 0

        return (
            <Box
                display="inline-flex"
                alignItems="center"
                gap={0.25}
                ml={0.75}
                sx={{
                    color: isPositive
                        ? "success.main"
                        : isNegative
                            ? "error.main"
                            : "text.secondary",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                }}
            >
                {isPositive && (
                    <Typography
                        component="span"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        +
                    </Typography>
                )}

                {isNegative && (
                    <Typography
                        component="span"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        -
                    </Typography>
                )}

                {!isPositive && !isNegative && (
                    <Typography
                        component="span"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        →
                    </Typography>
                )}

                {Math.abs(growth).toLocaleString("pt-BR", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                })}%
            </Box>
        )
    }


    /*
     * Numeric cell with growth
     */
    const renderValueWithGrowth = (current, previous) => {
        const value = Number(current) || 0

        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                width="100%"
                height="100%"
            >
                <Typography
                    component="span"
                    sx={{
                        fontSize: "inherit",
                    }}
                >
                    {value.toLocaleString("pt-BR")}
                </Typography>

                {renderGrowth(value, previous)}
            </Box>
        )
    }


    /*
     * Helpers for values that have fallback fields
     */
    const getMessages = (row) => {
        return (
            Number(row.meta_regular_messages) ||
            (
                Number(row.system_messages) || 0
            ) +
            (
                Number(row.agent_messages) || 0
            )
        )
    }


    const getMarketing = (row) => {
        return (
            Number(row.meta_marketing) ||
            Number(row.marketing) ||
            0
        )
    }


    const getUtility = (row) => {
        return (
            Number(row.meta_utility) ||
            Number(row.utility) ||
            0
        )
    }


    const getAuthentication = (row) => {
        return (
            Number(row.meta_authentication) ||
            Number(row.authentication) ||
            0
        )
    }


    /*
     * Previous month values
     *
     * Each row already contains:
     *
     * row.previous_billing
     *
     * So totals for the previous month can be calculated
     * from those records.
     */
    const previousTotals = {
        b2c: rows.reduce(
            (sum, row) =>
                sum +
                (
                    Number(row.previous_billing?.b2c) || 0
                ),
            0
        ),

        c2b: rows.reduce(
            (sum, row) =>
                sum +
                (
                    Number(row.previous_billing?.c2b) || 0
                ),
            0
        ),

        messages: rows.reduce(
            (sum, row) => {
                const previous = row.previous_billing

                if (!previous) {
                    return sum
                }

                return (
                    sum +
                    (
                        Number(previous.meta_regular_messages) ||
                        (
                            Number(previous.system_messages) || 0
                        ) +
                        (
                            Number(previous.agent_messages) || 0
                        )
                    )
                )
            },
            0
        ),

        marketing: rows.reduce(
            (sum, row) => {
                const previous = row.previous_billing

                if (!previous) {
                    return sum
                }

                return (
                    sum +
                    (
                        Number(previous.meta_marketing) ||
                        Number(previous.marketing) ||
                        0
                    )
                )
            },
            0
        ),

        utility: rows.reduce(
            (sum, row) => {
                const previous = row.previous_billing

                if (!previous) {
                    return sum
                }

                return (
                    sum +
                    (
                        Number(previous.meta_utility) ||
                        Number(previous.utility) ||
                        0
                    )
                )
            },
            0
        ),

        authentication: rows.reduce(
            (sum, row) => {
                const previous = row.previous_billing

                if (!previous) {
                    return sum
                }

                return (
                    sum +
                    (
                        Number(previous.meta_authentication) ||
                        Number(previous.authentication) ||
                        0
                    )
                )
            },
            0
        ),
    }


    /*
     * Current totals
     */
    const totals = {
        b2c: rows.reduce(
            (sum, row) =>
                sum +
                (Number(row.b2c) || 0),
            0
        ),

        c2b: rows.reduce(
            (sum, row) =>
                sum +
                (Number(row.c2b) || 0),
            0
        ),

        messages: rows.reduce(
            (sum, row) =>
                sum + getMessages(row),
            0
        ),

        marketing: rows.reduce(
            (sum, row) =>
                sum + getMarketing(row),
            0
        ),

        utility: rows.reduce(
            (sum, row) =>
                sum + getUtility(row),
            0
        ),

        authentication: rows.reduce(
            (sum, row) =>
                sum + getAuthentication(row),
            0
        ),
    }


    totals.total = totals.b2c + totals.c2b
    previousTotals.total = previousTotals.b2c + previousTotals.c2b


    /*
     * DataGrid columns
     */
    const columns = [
        {
            field: "enterprise_name",
            headerName: "Empresa",
            flex: 1,

            valueGetter: (value) => {
                return value.row.enterprise.name
            },
        },

        {
            field: "managers",
            headerName: "Gerentes de Conta",
            flex: 1,
        },

        {
            field: "total",
            headerName: "Atendimentos",
            flex: 1,
            type: "number",

            valueGetter: (value) => {
                return (
                    (Number(value.row.b2c) || 0) +
                    (Number(value.row.c2b) || 0)
                )
            },

            renderCell: (params) => {
                const current =
                    (Number(params.row.b2c) || 0) +
                    (Number(params.row.c2b) || 0)

                const previous =
                    (Number(params.row.previous_billing?.b2c) || 0) +
                    (Number(params.row.previous_billing?.c2b) || 0)

                return renderValueWithGrowth(
                    current,
                    params.row.previous_billing
                        ? previous
                        : null
                )
            },
        },

        {
            field: "b2c",
            headerName: "Ativos",
            flex: 1,
            type: "number",

            renderCell: (params) => {
                return renderValueWithGrowth(
                    params.row.b2c,
                    params.row.previous_billing?.b2c
                )
            },
        },

        {
            field: "c2b",
            headerName: "Receptivos",
            flex: 1,
            type: "number",

            renderCell: (params) => {
                return renderValueWithGrowth(
                    params.row.c2b,
                    params.row.previous_billing?.c2b
                )
            },
        },

        {
            field: "messages",
            headerName: "Mensagens",
            flex: 1,
            type: "number",

            valueGetter: (value) => {
                return getMessages(value.row)
            },

            renderCell: (params) => {
                const current = getMessages(params.row)

                const previous = params.row.previous_billing
                    ? getMessages(params.row.previous_billing)
                    : null

                return renderValueWithGrowth(
                    current,
                    previous
                )
            },
        },

        {
            field: "marketing",
            headerName: "Marketing",
            flex: 1,
            type: "number",

            valueGetter: (value) => {
                return getMarketing(value.row)
            },

            renderCell: (params) => {
                const current = getMarketing(params.row)

                const previous = params.row.previous_billing
                    ? getMarketing(params.row.previous_billing)
                    : null

                return renderValueWithGrowth(
                    current,
                    previous
                )
            },
        },

        {
            field: "utility",
            headerName: "Utilidades",
            flex: 1,
            type: "number",

            valueGetter: (value) => {
                return getUtility(value.row)
            },

            renderCell: (params) => {
                const current = getUtility(params.row)

                const previous = params.row.previous_billing
                    ? getUtility(params.row.previous_billing)
                    : null

                return renderValueWithGrowth(
                    current,
                    previous
                )
            },
        },

        {
            field: "authentication",
            headerName: "Autenticação",
            flex: 1,
            type: "number",

            valueGetter: (value) => {
                return getAuthentication(value.row)
            },

            renderCell: (params) => {
                const current = getAuthentication(params.row)

                const previous = params.row.previous_billing
                    ? getAuthentication(params.row.previous_billing)
                    : null

                return renderValueWithGrowth(
                    current,
                    previous
                )
            },
        },

        {
            field: "sms",
            headerName: "SMS",
            flex: 1,
            type: "number",

            renderCell: (params) => {
                return renderValueWithGrowth(
                    params.row.sms,
                    params.row.previous_billing?.sms
                )
            },
        },

        {
            field: "personal_whatsapp_card_registered_at",
            headerName: "Cartão Pessoal",
            flex: 1,
            type: "boolean",

            valueGetter: (value) => {
                const registeredAt =
                    value.row.enterprise.personal_whatsapp_card_registered_at

                if (!registeredAt) {
                    return false
                }

                return !period.isBefore(dayjs(registeredAt), "month")
            },

            renderCell: (params) => {
                const registeredAt =
                    params.row.enterprise.personal_whatsapp_card_registered_at

                let Icon = CancelIcon
                let color = "error.main"
                let tooltipTitle = "Não cadastrado no período selecionado." 

                if (registeredAt) {
                    const registeredDate = dayjs(registeredAt)

                    if (period.isBefore(registeredDate, "month")) {
                        // Period is before registration month
                        Icon = CancelIcon
                        color = "error.main"
                        tooltipTitle = "Não cadastrado no período selecionado."
                    } else if (period.isSame(registeredDate, "month")) {
                        // Period is registration month
                        Icon = ErrorIcon
                        color = "warning.main"
                        tooltipTitle = "Cadastrado no período selecionado."
                    } else {
                        // Period is after registration month
                        Icon = CheckCircleIcon
                        color = "success.main"
                        tooltipTitle = `Cadastrado em ${dayjs(registeredAt).format("DD/MM/YYYY HH:mm")}.`
                    }
                }

                return (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                    >
                        <Tooltip title={tooltipTitle}>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    const registeredAt =
                                        params.row.enterprise
                                            .personal_whatsapp_card_registered_at

                                    setPersonalCardDate(
                                        registeredAt
                                            ? dayjs(registeredAt)
                                            : dayjs()
                                    )

                                    setPersonalCardDialog(
                                        params.row.enterprise.id
                                    )
                                }}
                            >
                                <Icon
                                    fontSize="small"
                                    sx={{
                                        color,
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            },
        },

        {
            field: "charged_at",
            headerName: "Faturado",
            flex: 1,
            type: "boolean",

            valueGetter: (value) => {
                return Boolean(value.row.charged_at)
            },

            renderCell: (params) => {
                const chargedAt = params.row.charged_at

                const tooltipTitle = chargedAt
                    ? `Faturado em ${dayjs(chargedAt).format("DD/MM/YYYY HH:mm")} por ${params.row.charged_by}.`
                    : "Não faturado."

                return (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                    >
                        <Tooltip title={tooltipTitle}>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    const chargedAt =
                                        params.row.enterprise.charged_at

                                    setChargedDate(
                                        chargedAt
                                            ? dayjs(chargedAt)
                                            : dayjs()
                                    )

                                    setChargedDialog(
                                        params.row.id
                                    )
                                }}
                            >
                                {chargedAt
                                    ? (
                                        <CheckCircleIcon
                                            fontSize="small"
                                            sx={{
                                                color: "success.main",
                                            }}
                                        />
                                    )
                                    : (
                                        <CancelIcon
                                            fontSize="small"
                                            sx={{
                                                color: "error.main",
                                            }}
                                        />
                                    )
                                }
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            },
        },
    ]


    /*
     * Toolbar
     */
    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                className={gridClasses.toolbarContainer}
            >
                <GridToolbarFilterButton />
                <GridToolbarExport />
            </GridToolbarContainer>
        )
    }


    /*
     * Personal card
     */
    const handlePersonalCardChange = async (enterpriseId, value) => {
        setLoading(true)

        try {
            const res = await api.patch(
                "api/metrics/enterprise/",
                {
                    enterprise_id: enterpriseId,
                    personal_whatsapp_card_registered_at: value.format("YYYY-MM-DD HH:mm:ss"),
                }
            )

            if (res.status === 200) {
                setData((current) =>
                    current.map((item) =>
                        item.enterprise.id === enterpriseId
                            ? {
                                ...item,
                                enterprise: {
                                    ...item.enterprise,
                                    has_personal_whatsapp_card:
                                        value,
                                },
                            }
                            : item
                    )
                )

                setPersonalCardDialog(null)

                useNotification(
                    "Sucesso",
                    "Empresa atualizada com sucesso.",
                    "success"
                )

                return
            }

            useNotification(
                "Ops",
                "Algo deu errado.",
                "danger"
            )
        } catch (err) {
            console.error(err)

            useNotification(
                "Ops",
                "Algo deu errado.",
                "danger"
            )
        } finally {
            setLoading(false)
        }
    }


    /*
     * Charged
     */
    const handleChargedChange = async (billingId, chargedDate) => {
        setLoading(true)

        try {
            const res = await api.patch(
                "api/metrics/billing/",
                {
                    billing_id: billingId,
                    charged_at: chargedDate
                        ? chargedDate.format("YYYY-MM-DD HH:mm:ss")
                        : null,
                    charged_by: localStorage.getItem("@kwikapi-admin-username"),
                }
            )

            if (res.status === 200) {
                setData((current) =>
                    current.map((item) =>
                        item.id === billingId
                            ? {
                                ...item,
                                charged_at: chargedDate
                                    ? chargedDate.format("YYYY-MM-DD HH:mm:ss")
                                    : null,
                            }
                            : item
                    )
                )

                setChargedDialog(null)

                useNotification(
                    "Sucesso",
                    "Empresa atualizada com sucesso.",
                    "success"
                )

                return
            }

            useNotification(
                "Ops",
                "Algo deu errado.",
                "danger"
            )
        } catch (err) {
            console.error(err)

            useNotification(
                "Ops",
                "Algo deu errado.",
                "danger"
            )
        } finally {
            setLoading(false)
        }
    }


    /*
     * Totals cards
     */
    const totalCards = [
        {
            label: "Atendimentos",
            value: totals.total,
            previous: previousTotals.total,
        },
        {
            label: "Ativos",
            value: totals.b2c,
            previous: previousTotals.b2c,
        },
        {
            label: "Receptivos",
            value: totals.c2b,
            previous: previousTotals.c2b,
        },
        {
            label: "Mensagens",
            value: totals.messages,
            previous: previousTotals.messages,
        },
        {
            label: "Marketing",
            value: totals.marketing,
            previous: previousTotals.marketing,
        },
        {
            label: "Utilidades",
            value: totals.utility,
            previous: previousTotals.utility,
        },
        {
            label: "Autenticação",
            value: totals.authentication,
            previous: previousTotals.authentication,
        },
    ]


    return (
        <>
            <Loading loading={loading} />

            <Box
                display="flex"
                flexDirection="column"
                height="100%"
                minHeight={0}
            >

                {/* Header */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    padding={2}
                    paddingBottom={1}
                >

                    {/* Month / Year */}
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="pt-br"
                    >
                        <DatePicker
                            label="Mês"
                            views={["year", "month"]}
                            value={period}
                            onChange={(newValue) => {
                                if (newValue) {
                                    setPeriod(newValue)
                                }
                            }}
                            slotProps={{
                                textField: {
                                    size: "small",
                                },
                            }}
                            sx={{
                                width: "12.5rem",
                            }}
                        />
                    </LocalizationProvider>


                    {/* Totals */}
                    <Box
                        display="flex"
                        alignItems="stretch"
                        gap={1}
                    >
                        {totalCards.map((item) => (
                            <Box
                                key={item.label}
                                sx={{
                                    width: "160px",
                                    py: 0.75,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1.5,
                                    backgroundColor:
                                        "background.paper",
                                    textAlign: "center",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    noWrap
                                >
                                    {item.label}
                                </Typography>

                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        lineHeight={1.2}
                                    >
                                        {item.value.toLocaleString(
                                            "pt-BR"
                                        )}
                                    </Typography>

                                    {renderGrowth(
                                        item.value,
                                        item.previous
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>


                {/* DataGrid */}
                <Box
                    flex={1}
                    minHeight={0}
                    padding={2}
                    paddingTop={0}
                >
                    <ThemeProvider theme={theme}>
                        <DataGridPro
                            pagination
                            initialState={{
                                pagination: {
                                    pageSize: 25,
                                },
                            }}
                            disableSelectionOnClick
                            disableDensitySelector
                            columns={columns}
                            rows={rows}
                            loading={loading}
                            density="compact"
                            components={{
                                Toolbar: CustomToolbar,
                            }}
                        />
                    </ThemeProvider>
                </Box>

            </Box>


            {/* Personal Card Dialog */}
            <Dialog
                open={Boolean(personalCardDialog)}
                onClose={() => {
                    setPersonalCardDialog(null)
                }}
            >
                <DialogTitle>
                    Cartão Pessoal
                </DialogTitle>

                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Informe a data e hora do registro do cartão pessoal.
                    </Typography>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="pt-br"
                    >
                        <DateTimePicker
                            label="Data e hora"
                            value={personalCardDate}
                            onChange={(value) => {
                                setPersonalCardDate(value)
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: "small",
                                },
                            }}
                        />
                    </LocalizationProvider>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => {
                            setPersonalCardDialog(null)
                        }}
                    >
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => {
                            handlePersonalCardChange(
                                personalCardDialog,
                                personalCardDate
                            )
                        }}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>


            {/* Charged Dialog */}
            <Dialog
                open={Boolean(chargedDialog)}
                onClose={() => {
                    setChargedDialog(null)
                }}
            >
                <DialogTitle>
                    Faturamento
                </DialogTitle>

                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Informe a data e hora do faturamento.
                    </Typography>

                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="pt-br"
                    >
                        <DateTimePicker
                            label="Data e hora do faturamento"
                            value={chargedDate}
                            onChange={(value) => {
                                setChargedDate(value)
                            }}
                            format="DD/MM/YYYY HH:mm"
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: "small",
                                },
                            }}
                        />
                    </LocalizationProvider>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => {
                            setChargedDialog(null)
                        }}
                    >
                        Cancelar
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => {
                            handleChargedChange(
                                chargedDialog,
                                chargedDate
                            )
                        }}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}


export default WhatsApp